import { Link } from 'react-router-dom';
import type { VideoCard as VideoCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDuration } from '@/lib/format';
import { ArticleImage } from '@/components/content';
import { Icon } from '@/components/shell';
import { cn } from '@/components/ui';

/**
 * One video, as a card — PRD §13.
 *
 * The Phase 12 DoD asks that video cards be "visually identical wherever they appear",
 * so this is the only place one is drawn: the homepage strip, the `/videos` grid, the
 * related rail on a video page and the rail on an article that has clips attached all
 * render this component. The homepage strip used to own a copy of this markup; it now
 * calls here, which is what makes the promise keepable rather than a thing to remember.
 *
 * `variant="short"` is the portrait card a Short deserves — the same card, in the shape
 * of its video.
 */
export function VideoCard({
  video,
  variant = 'default',
  className,
}: {
  video: VideoCardPayload;
  variant?: 'default' | 'short';
  className?: string;
}) {
  const { locale, t } = useLocale();
  const short = variant === 'short';

  return (
    <Link
      to={video.path}
      data-video-card={variant}
      className={cn('group block', short && 'w-40 shrink-0 sm:w-44', className)}
    >
      <span
        className={cn(
          'relative block overflow-hidden rounded-lg bg-soft',
          short ? 'aspect-[9/16]' : 'aspect-video',
        )}
      >
        <Thumbnail video={video} short={short} />

        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,0,.35),transparent_55%)]"
        />

        {video.durationSeconds ? (
          <span className="absolute left-2.5 top-2.5 z-10 rounded-sm bg-black/70 px-1.5 py-0.5 text-2xs font-bold text-white">
            {formatDuration(video.durationSeconds, locale)}
          </span>
        ) : null}

        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 z-10 grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/85 transition group-hover:scale-110"
        >
          <Icon name="play" size={15} className="ml-0.5 text-red" />
        </span>
        <span className="sr-only">{t('action.play')}</span>
      </span>

      <span
        className={cn(
          'mt-2.5 block font-bold text-ink transition group-hover:text-red',
          short ? 'text-sm' : 'text-base',
        )}
      >
        {video.title}
      </span>
    </Link>
  );
}

/**
 * The editor's uploaded override when there is one (an ordinary image with our own
 * variants, ADR-006), and the provider's remote thumbnail otherwise — which is a plain
 * `<img>`, because a URL on someone else's CDN has no variants of ours to choose from.
 */
function Thumbnail({ video, short }: { video: VideoCardPayload; short: boolean }) {
  const sizes = short ? '176px' : '(max-width: 720px) 100vw, 25vw';

  if (video.thumbnail) {
    return (
      <ArticleImage
        image={video.thumbnail}
        alt={video.title}
        aspect={short ? 'aspect-[9/16]' : 'aspect-video'}
        sizes={sizes}
        className="transition duration-300 group-hover:scale-105"
      />
    );
  }

  if (video.thumbnailUrl) {
    return (
      <img
        src={video.thumbnailUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
      />
    );
  }

  return <span className="block h-full w-full bg-soft" aria-hidden="true" />;
}
